import React, { useState, useEffect, Suspense, lazy, useCallback } from "react";
import {
  IonApp,
  IonRouterOutlet,
  IonSplitPane,
  setupIonicReact,
  isPlatform,
} from "@ionic/react";
import { Route, useLocation, useHistory } from "react-router-dom";
import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";
import "./theme/variables.css";
import "./css/style.css";

import { PrivacyScreen } from "@capacitor-community/privacy-screen";
import { AdMob, BannerAdSize } from "@capacitor-community/admob";
import { Capacitor } from "@capacitor/core";

import Menu from "./components/Menu";
import SplashScreen from "./components/SplashScreen";
import { Network } from "@capacitor/network";
import { getStateList } from "./modalconfig";

const Home = lazy(() => import("./pages/Home"));
const Registration = lazy(() => import("./pages/Registration"));
const ViewMap = lazy(() => import("./pages/ViewMap"));
const PlaceInformation = lazy(() => import("./pages/PlaceInformation"));
const CityList = lazy(() => import("./pages/cityList"));
const ParticularPlaceInformation = lazy(() => import("./pages/ParticularPlaceInformation"));
const ViewHistoricalPlace = lazy(() => import("./pages/ViewHistoricalPlace"));
const ViewDetails = lazy(() => import("./pages/ViewDetails"));
const ViewPlacesCard = lazy(() => import("./pages/ViewPlacesCard"));
const Downloads = lazy(() => import("./pages/Downloads"));
const Blogs = lazy(() => import("./pages/Blogs"));
const BlogDetails = lazy(() => import("./pages/BlogDetails"));
const ViewSpecialEvents = lazy(() => import("./pages/ViewSpecialEvents"));
const SpecialEventDetails = lazy(() => import("./pages/SpecialEventDetails"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const VerifyLogin = lazy(() => import("./pages/VerifyLogin"));
const ForgotPassword = lazy(() => import("./pages/Forgot-Password"));

setupIonicReact();

const App = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [user, setUser] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const [langCode, setLangCode] = useState("en");

  const location = useLocation();
  const history = useHistory();

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  const enablePrivacyScreen = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        if (isPlatform("ios")) {
          console.log("Enabling privacy screen for iOS");
          await PrivacyScreen.enable().catch(async (error) => {
            console.warn("PrivacyScreen enable failed:", error);
            try {
              if (typeof PrivacyScreen?.enable === "function") {
                await PrivacyScreen.enable();
              }
            } catch (iosError) {
              console.error("iOS PrivacyScreen failed:", iosError);
            }
          });
        } else {
          console.log("Enabling privacy screen for Android");
          await PrivacyScreen.enable();
        }
        console.log("Privacy screen enabled successfully");
      }
    } catch (error) {
      console.error("Failed to enable privacy screen:", error);
    }
  };

  document.addEventListener("deviceready", async () => {
    if (Capacitor.getPlatform() === "ios") {
      try {
        await PrivacyScreen.enable();
        console.log("✅ Privacy screen enabled on iOS");
      } catch (err) {
        console.error("❌ Privacy screen failed:", err);
      }
    }
  });

  const handleAppStateChange = useCallback(() => {
    if (document.visibilityState === "visible") {
      enablePrivacyScreen();
    }
  }, []);

  const showBannerAd = async () => {
    var adId = '';
    if (Capacitor.isNativePlatform()) {
      if (isPlatform('ios')) {
        adId = 'ca-app-pub-3413143877119845/4113173850';
      } else {
        adId = 'ca-app-pub-3413143877119845/8825386979';
      }
    } else {
      adId = 'ca-app-pub-3413143877119845/8825386979';
    }


    try {
      await AdMob.showBanner({
        adId: adId,
        adSize: BannerAdSize.ADAPTIVE_BANNER,
        margin: 85,
        bannerAtTop: true,
        overlap: true,
      });
    } catch (error) {
      console.error("Banner ad error:", error);
    }
  };

  const logCurrentNetworkStatus = async () => {
    try {
      const status = await Network.getStatus();
      localStorage.setItem("Networkstatus", status.connected);
    } catch (error) {
      console.error("Error checking network status:", error);
    }
  };

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
    }
    if ("caches" in window) {
      caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
    }
  }, []);

  useEffect(() => {
    const splashTimeout = setTimeout(() => {
      setShowSplash(false);
      setTimeout(() => {
        AdMob.initialize({
          requestTrackingAuthorization: true,
          initializeForTesting: false,
        });
        enablePrivacyScreen();
        showBannerAd();
      }, 1500);
    }, 2500);
    logCurrentNetworkStatus();

    const networkStatusChangeListener = Network.addListener("networkStatusChange", (status) => {
      localStorage.setItem("Networkstatus", status.connected);
    });

    document.addEventListener("visibilitychange", handleAppStateChange);
    window.addEventListener("pagehide", handleAppStateChange);
    window.addEventListener("pageshow", handleAppStateChange);
    window.addEventListener("resize", handleResize);

    const language = localStorage.getItem("languageCode") || "en";
    setLangCode(language);
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "null");
    if (userDetails) setUser(userDetails);

    return () => {
      clearTimeout(splashTimeout);
      networkStatusChangeListener?.remove();
      document.removeEventListener("visibilitychange", handleAppStateChange);
      window.removeEventListener("pagehide", handleAppStateChange);
      window.removeEventListener("pageshow", handleAppStateChange);
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize, handleAppStateChange]);

  useEffect(() => {
    const getCities = async () => {
      const res = await getStateList({});
      if (Array.isArray(res?.city) && res.city.length > 0) {
        localStorage.setItem("cities", JSON.stringify(res.city));
      }
    };
    getCities();
  }, []);

  useEffect(() => {
    if (isPlatform("ios") && Capacitor.isNativePlatform()) {
      const iosPrivacyScreenRetry = setTimeout(() => {
        enablePrivacyScreen();
      }, 3000);
      return () => clearTimeout(iosPrivacyScreenRetry);
    }
  }, []);

  if (showSplash) return <SplashScreen />;

  return (
    <IonApp className="no-safe-area" >
      <IonSplitPane when="false" contentId="main">
        <Menu />
        <IonRouterOutlet id="main" animated={false}>
          <Suspense fallback={<SplashScreen />}>
            <Route path="/" exact component={Home} />
            <Route path="/home" exact component={Home} />
            <Route path="/registration" exact component={Registration} />
            <Route path="/viewmap/:name" exact component={ViewMap} />
            <Route path="/citylist" exact component={CityList} />
            <Route path="/placeinformation" exact component={PlaceInformation} />
            <Route path="/particularplaceinformation" exact component={ParticularPlaceInformation} />
            <Route path="/viewplacescard" exact component={ViewPlacesCard} />
            <Route path="/viewspecialevents/:_id" exact component={ViewSpecialEvents} />
            <Route path="/specialeventdetails/:placeId" exact component={SpecialEventDetails} />
            <Route path="/viewDetails" exact component={ViewDetails} />
            <Route path="/viewhistoricalplace/:name" exact component={ViewHistoricalPlace} />
            <Route path="/downloads" exact component={Downloads} />
            <Route path="/blogs" exact component={Blogs} />
            <Route path="/blogdetails/:pathId" exact component={BlogDetails} />
            <Route path="/settings" exact component={Settings} />
            <Route path="/profile" exact component={Profile} />
            <Route path="/edit-profile" exact component={EditProfile} />
            <Route path="/change-password" exact component={ChangePassword} />
            <Route path="/verify-Login/:token" exact component={VerifyLogin} />
            <Route path="/forgot-password/:id/:token" exact component={ForgotPassword} />
          </Suspense>
        </IonRouterOutlet>
      </IonSplitPane>
    </IonApp>
  );
};

export default App;